import { useState, useEffect } from "react";
import {
  getAvailableDataSources,
  extractTableData,
  validateTableData,
  ExcelTableData,
} from "../../../shared/excelDataExtractor";
import {
  smartColumnApiService,
  ColumnSuggestion,
  ColumnMetadata,
  ApiError,
  default as SmartColumnApiService,
} from "../../../shared/smartColumnApiService";
import { createNewSheetWithResults } from "./sheetCreationUtils";
import useSmartColumnApiProgress from "./SmartColumnApiProgress";
import { POLLING, JOB_STATUS } from "../../../shared/constants";

export const useSmartColumn = () => {
  const [selectedSource, setSelectedSource] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [tableOptions, setTableOptions] = useState<string[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(true);
  const [userIntent, setUserIntent] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [selectedTableData, setSelectedTableData] = useState<ExcelTableData | null>(null);
  const [allDataRows, setAllDataRows] = useState<any[][]>([]);
  const [suggestions, setSuggestions] = useState<ColumnSuggestion[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  const [isInserting, setIsInserting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [apiResults, setApiResults] = useState<Array<{ [key: string]: any }> | null>(null);
  const [apiReturnedColumns, setApiReturnedColumns] = useState<string[] | null>(null);
  const [apiColumnMetadata, setApiColumnMetadata] = useState<ColumnMetadata[] | null>(null);
  const { progress, result, startGeneration, isLoading, stopListening } =
    useSmartColumnApiProgress();

  useEffect(() => {
    const loadDataSources = async () => {
      try {
        setIsLoadingOptions(true);
        const sources = await getAvailableDataSources();
        setTableOptions(sources);

        if (sources.length === 0) {
          console.warn("No data sheets found.");
        }
      } catch (error) {
        console.error("Error loading data sources:", error);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadDataSources();
  }, []);

  useEffect(() => {
    const POLL_INTERVAL_MS = POLLING.SMART_COLUMN_INTERVAL_MS;

    const pollForNewSheets = async () => {
      try {
        const currentSources = await getAvailableDataSources();

        setTableOptions((prevOptions) => {
          const newSheets = currentSources.filter((sheet) => !prevOptions.includes(sheet));

          if (newSheets.length > 0) {
            console.log(`🔄 New sheets detected: ${newSheets.join(", ")}`);
            return currentSources;
          }

          const removedSheets = prevOptions.filter((sheet) => !currentSources.includes(sheet));
          if (removedSheets.length > 0) {
            console.log(`🔄 Sheets removed: ${removedSheets.join(", ")}`);
            return currentSources;
          }

          return prevOptions;
        });
      } catch (error) {
        console.warn("Error polling for new sheets:", error);
      }
    };

    const pollInterval = setInterval(pollForNewSheets, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollInterval);
    };
  }, []);

  const handleSourceSelect = async (source: string) => {
    setSelectedSource(source);
    setDropdownOpen(false);
    setShowValidation(false);
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setAllDataRows([]);
    setError("");
    setApiResults(null);
    setApiReturnedColumns(null);
    setApiColumnMetadata(null);

    try {
      const tableData = await extractTableData(source);
      console.log("Extracted table data:", tableData);

      const validation = validateTableData(tableData);

      if (!validation.valid) {
        console.error("Invalid table data:", validation.message);
        setError("Invalid table data. Please select a different source.");
        return;
      }

      setSelectedTableData(tableData);
      setAllDataRows(tableData.data || []);
    } catch (error) {
      console.error("Error extracting table data:", error);
      setError("Failed to extract table data. Please try again.");
    }
  };

  const toggleSuggestion = (index: number) => {
    setSelectedSuggestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleGenerate = async () => {
    if (!selectedSource) {
      setShowValidation(true);
      return;
    }

    if (!userIntent.trim()) {
      setError("Please enter an intent describing what kind of columns to generate.");
      return;
    }

    if (!selectedTableData) {
      setError("No table data available. Please select a source.");
      return;
    }

    if (!selectedTableData.headers || selectedTableData.headers.length === 0) {
      setError("Table has no headers. Please select a valid table.");
      return;
    }

    const dataRows = selectedTableData.data || [];
    if (dataRows.length === 0) {
      setError("Table has no data rows. Please select a table with data.");
      return;
    }

    try {
      setIsGenerating(true);
      setError("");
      setSuggestions([]);
      setSelectedSuggestions(new Set());
      stopListening();

      const totalRows = dataRows.length;

      console.log(`📊 Sending all rows to backend:`);
      console.log(`   Total rows: ${totalRows}`);
      console.log(`   Columns: ${selectedTableData.headers.length}`);

      const requestPayload = {
        table_name: selectedSource,
        column_headers: selectedTableData.headers,
        rows: dataRows,
        user_intent: userIntent,
        total_row_count: dataRows.length,
      };

      console.log("Starting smart column generation with progress tracking...");
      await startGeneration(requestPayload);
    } catch (error) {
      console.error("Error generating columns:", error);
      const errorMessage = SmartColumnApiService.formatErrorMessage(error as ApiError);
      setError(errorMessage);
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    const handleResult = async () => {
      if (result) {
        console.log("Smart Column result received:", result);
        const apiResponse = result as any;

        try {
          if (apiResponse.status === JOB_STATUS.FAILED || apiResponse.error) {
            console.error("❌ Backend validation/generation failed:", apiResponse.error);
            const errorMessage = apiResponse.error || "Generation failed. Please try again.";
            setError(errorMessage);
            setIsGenerating(false);
            return;
          }

          if (apiResponse.returned_columns && apiResponse.results && apiResponse.column_metadata) {
            console.log(
              `✅ Received new response structure with ${apiResponse.returned_columns.length} columns and ${apiResponse.results.length} rows`
            );

            if (apiResponse.results.length > 0) {
              const firstRow = apiResponse.results[0];
              console.log("🔍 First row keys:", Object.keys(firstRow));
              console.log(
                "🔍 First row Composite_Performance_Score:",
                firstRow["Composite_Performance_Score"]
              );
              console.log("🔍 First row sample:", JSON.stringify(firstRow).substring(0, 500));
            }

            console.log(
              "🔍 Column metadata:",
              apiResponse.column_metadata.map((m) => m.column_name)
            );
            const compositeMeta = apiResponse.column_metadata.find(
              (m) => m.column_name === "Composite_Performance_Score"
            );
            if (compositeMeta) {
              console.log("🔍 Found Composite_Performance_Score in metadata:", compositeMeta);
            } else {
              console.log("⚠️ Composite_Performance_Score NOT in column_metadata!");
            }

            console.log("🔍 Returned columns:", apiResponse.returned_columns);
            if (apiResponse.returned_columns.includes("Composite_Performance_Score")) {
              console.log("✅ Composite_Performance_Score IS in returned_columns");
            } else {
              console.log("⚠️ Composite_Performance_Score NOT in returned_columns!");
            }

            setApiResults(apiResponse.results);
            setApiReturnedColumns(apiResponse.returned_columns);
            setApiColumnMetadata(apiResponse.column_metadata);

            const originalHeaders = selectedTableData?.headers || [];
            const newColumns = apiResponse.returned_columns.filter(
              (col) => !originalHeaders.includes(col)
            );

            console.log(`📊 Found ${newColumns.length} new generated columns:`, newColumns);

            if (newColumns.includes("Composite_Performance_Score")) {
              console.log("✅ Composite_Performance_Score is in newColumns");
            } else {
              console.log("⚠️ Composite_Performance_Score NOT in newColumns!");
              console.log("   This means it was either:");
              console.log("   1. Not in returned_columns from backend");
              console.log("   2. Or it matches an original header (unlikely)");
            }

            // ✅ Create a map of column metadata for quick lookup (including formulas!)
            const metadataMap = new Map<string, ColumnMetadata>();
            apiResponse.column_metadata.forEach((meta) => {
              metadataMap.set(meta.column_name, meta);
            });

            const convertedSuggestions: ColumnSuggestion[] = newColumns.map((columnName) => {
              // Get metadata for this column
              const metadata = metadataMap.get(columnName);
              const description = metadata?.description || `Generated column: ${columnName}`;
              const columnType = metadata?.column_type || "computed";
              const formula = metadata?.formula;
              const computation_logic = metadata?.computation_logic;

              const columnValues = apiResponse
                .results!.map((row) => row[columnName])
                .filter((v) => v !== null && v !== undefined);

              return {
                column_name: columnName,
                column_type: columnType,
                formula: formula,
                computed_values: columnValues,
                computation_logic: computation_logic,
                description: description,
              };
            });

            setSuggestions(convertedSuggestions);

            const allIndices = new Set(convertedSuggestions.map((_, i) => i));
            setSelectedSuggestions(allIndices);

            console.log(
              `✅ Converted to ${convertedSuggestions.length} column suggestions for user selection`
            );
            console.log(
              `   Formula columns: ${convertedSuggestions
                .filter((s) => s.column_type === "formula")
                .map((s) => s.column_name)
                .join(", ")}`
            );
            console.log(
              `   Computed columns: ${convertedSuggestions
                .filter((s) => s.column_type === "computed")
                .map((s) => s.column_name)
                .join(", ")}`
            );
          } else {
            console.error(
              "❌ Unexpected smart column API response structure. Expected returned_columns, results, and column_metadata."
            );
            setError(
              "Unexpected API response. Please try again or contact support if the issue persists."
            );
          }
        } catch (error) {
          console.error("Error processing result:", error);
          setError("Failed to process generation results.");
        } finally {
          setIsGenerating(false);
        }
      }
    };
    handleResult();
  }, [result]);

  const handleInsertColumns = async () => {
    if (selectedSuggestions.size === 0) {
      setError("Please select at least one column to insert.");
      return;
    }

    try {
      setIsInserting(true);
      setError("");

      if (!apiResults || !apiReturnedColumns || !apiColumnMetadata) {
        console.error(
          "❌ Missing API results/columns/metadata when inserting columns. This should not happen with the new backend contract."
        );
        setError(
          "Unable to insert columns due to missing API results. Please re-run generation and try again."
        );
        return;
      }

      const selectedColumnNames = Array.from(selectedSuggestions)
        .map((index) => suggestions[index]?.column_name)
        .filter(Boolean) as string[];

      const originalHeaders = selectedTableData?.headers || [];
      const columnsToInclude = [...originalHeaders, ...selectedColumnNames];

      const filteredResults = apiResults.map((row) => {
        const filteredRow: { [key: string]: any } = {};
        columnsToInclude.forEach((col) => {
          filteredRow[col] = row[col];
        });
        return filteredRow;
      });

      const filteredMetadata = apiColumnMetadata.filter((meta) =>
        selectedColumnNames.includes(meta.column_name)
      );

      await createNewSheetWithResults(
        filteredResults,
        columnsToInclude,
        filteredMetadata,
        selectedSource
      );

      console.log(`✅ Successfully created sheet with ${filteredResults.length} rows`);

      setSuggestions([]);
      setSelectedSuggestions(new Set());
      setApiResults(null);
      setApiReturnedColumns(null);
      setApiColumnMetadata(null);
    } catch (error) {
      console.error("Error inserting columns:", error);

      const errorMessage = error instanceof Error ? error.message : String(error);
      if (
        errorMessage.includes("PivotTable") ||
        errorMessage.includes("query results") ||
        errorMessage.includes("protected cells") ||
        errorMessage.includes("another table")
      ) {
        console.warn(
          "Sheet created successfully, but Excel Table could not be created:",
          errorMessage
        );
        setSuggestions([]);
        setSelectedSuggestions(new Set());
        setApiResults(null);
        setApiReturnedColumns(null);
        setApiColumnMetadata(null);
        setError(
          "Columns inserted successfully. Note: Excel Table could not be created (may overlap with existing PivotTable or table)."
        );
      } else {
        console.error(`Failed to insert columns: ${errorMessage}`);
        setError(`Failed to insert columns: ${errorMessage}`);
      }
    } finally {
      setIsInserting(false);
    }
  };

  return {
    selectedSource,
    dropdownOpen,
    setDropdownOpen,
    showValidation,
    tableOptions,
    isLoadingOptions,
    userIntent,
    setUserIntent,
    isGenerating,
    selectedTableData,
    allDataRows,
    suggestions,
    selectedSuggestions,
    isInserting,
    error,
    handleSourceSelect,
    toggleSuggestion,
    handleGenerate,
    handleInsertColumns,
    progress,
    isLoadingProgress: isLoading,
  };
};
